import { useMemo, useState } from "react";

import { TransactionType } from "../../../../utils";
import { useInsightsData } from "../../hooks/useInsightsData";
import { CashflowChart } from "./CashflowChart";
import { CategoriesBreakdown } from "./CategoriesBreakdown";
import { InsightsEmptyState } from "./InsightsEmptyState";
import {
  Body,
  CollapseButton,
  Header,
  HeaderPills,
  HeaderTitleBlock,
  Panel,
  PanelSubtitle,
  PanelTitle,
  Section,
  Title,
} from "./styles";
import { InsightsProps } from "./types";

const buildPills = (filters: InsightsProps["filters"]): string => {
  const parts: string[] = [];
  if (filters.account) {
    parts.push(filters.account.label);
  }
  if (filters.category) {
    parts.push(filters.category.label);
  }
  if (filters.searchBy) {
    parts.push(`"${filters.searchBy}"`);
  }
  return parts.join(" · ");
};

export const Insights: React.FC<InsightsProps> = ({
  period,
  filters,
  debouncedSearch,
  isCompact,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const isTransfer = filters.type.value === TransactionType.TRANSFER;

  const { window, series, categories, isLoading, hasData } = useInsightsData({
    period,
    filters,
    debouncedSearch,
    isCompact,
    enabled: !isTransfer,
  });

  const pills = useMemo(() => buildPills(filters), [filters]);
  const showIncome = filters.type.value !== TransactionType.EXPENSE;
  const showExpense = filters.type.value !== TransactionType.INCOME;
  const isIncomeType = filters.type.value === TransactionType.INCOME;
  const categoriesTitle = isIncomeType
    ? "Top income categories"
    : "Top expense categories";

  if (isTransfer) {
    return null;
  }

  const summary = hasData ? pills : "Not enough data for this selection";
  const expandedText = `Cashflow and category breakdown`;
  const headerText = collapsed ? summary : expandedText;
  const showEmpty = !collapsed && !hasData && !isLoading;
  const showBody = !collapsed && (hasData || isLoading);
  const toggleCollapsed = (): void => {
    setCollapsed((value) => !value);
  };

  return (
    <Section aria-label="Insights">
      <Header>
        <HeaderTitleBlock>
          <Title>Insights</Title>
          <HeaderPills>{headerText}</HeaderPills>
        </HeaderTitleBlock>
        <CollapseButton type="button" onClick={toggleCollapsed}>
          {collapsed ? "Expand" : "Collapse"}
        </CollapseButton>
      </Header>

      {showEmpty && <InsightsEmptyState />}

      {showBody && (
        <Body>
          <Panel>
            <PanelTitle>Cashflow</PanelTitle>
            <PanelSubtitle>{window.cashflowSubtitle}</PanelSubtitle>
            <CashflowChart
              slots={window.slots}
              series={series}
              subtitle={window.cashflowSubtitle}
              showIncome={showIncome}
              showExpense={showExpense}
            />
          </Panel>
          <Panel>
            <PanelTitle>{categoriesTitle}</PanelTitle>
            <PanelSubtitle>{window.categoriesSubtitle}</PanelSubtitle>
            <CategoriesBreakdown
              points={categories}
              isExpense={!isIncomeType}
            />
          </Panel>
        </Body>
      )}
    </Section>
  );
};
